import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {App as AntdApp, Empty} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading, PageHeader} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetProjectsQuery,
	useCreateProjectMutation,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
} from "~/reduxs/api/projectApiSlice";
import ProjectFormModal from "../components/ProjectFormModal";
import style from "../style/Project.module.scss";

const cn = className.bind(style);

/**
 * Danh sách Dự án. Mỗi dự án là một website, chứa nhiều công cụ SEO (Site Crawler,
 * Google Search Console, ...). Bấm vào dự án → dashboard chọn công cụ.
 */
function ProjectList() {

	const navigate = useNavigate();
	const {notification, modal} = AntdApp.useApp();

	const {data: projects = [], isLoading} = useGetProjectsQuery();
	const [createProject, {isLoading: creating}] = useCreateProjectMutation();
	const [updateProject, {isLoading: updating}] = useUpdateProjectMutation();
	const [deleteProject] = useDeleteProjectMutation();

	const [openCreate, setOpenCreate] = useState(false);
	const [editing, setEditing] = useState(null);

	const handleCreate = async (data) => {
		try {
			const project = await createProject(data).unwrap();
			notification.success({message: 'Thành công', description: 'Đã tạo dự án'});
			setOpenCreate(false);
			if (project?.id) navigate(`/projects/${project.id}`);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Tạo dự án thất bại')});
		}
	};

	const handleUpdate = async (data) => {
		try {
			await updateProject({id: editing.id, ...data}).unwrap();
			notification.success({message: 'Thành công', description: 'Đã cập nhật dự án'});
			setEditing(null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Cập nhật thất bại')});
		}
	};

	const handleDelete = (e, project) => {
		e.stopPropagation();
		modal.confirm({
			title: 'Xóa dự án',
			content: `Xóa dự án "${project.name}" và toàn bộ dữ liệu công cụ bên trong?`,
			okText: 'Xóa',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deleteProject(project.id).unwrap();
					notification.success({message: 'Thành công', description: 'Đã xóa dự án'});
				} catch (err) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(err, 'Xóa thất bại')});
				}
			},
		});
	};

	const openEdit = (e, project) => {
		e.stopPropagation();
		setEditing(project);
	};

	if (isLoading) return <Loading />;

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-folder-tree"
				title="Dự án"
				subtitle="Mỗi dự án là một website, chứa nhiều công cụ SEO"
				actions={
					<Button primary onClick={() => setOpenCreate(true)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
						Tạo dự án
					</Button>
				}
			/>

			{projects.length === 0 ? (
				<Empty description="Chưa có dự án nào. Tạo dự án đầu tiên để bắt đầu.">
					<Button primary onClick={() => setOpenCreate(true)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
						Tạo dự án
					</Button>
				</Empty>
			) : (
				<div className={cn('grid')}>
					{projects.map((p) => (
						<div key={p.id} className={cn('projectCard')} onClick={() => navigate(`/projects/${p.id}`)}>
							<div className={cn('cardHead')}>
								<h3 className={cn('name')}>{p.name}</h3>
								<div className={cn('tableActions')}>
									<Button small blue onClick={(e) => openEdit(e, p)}
										leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />} />
									<Button small red onClick={(e) => handleDelete(e, p)}
										leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
								</div>
							</div>
							<div className={cn('url')}>{p.root_url}</div>
							<div style={{color: '#4f46e5', fontSize: 13}}>
								<FontAwesomeIcon icon="fa-light fa-toolbox" /> Mở công cụ →
							</div>
						</div>
					))}
				</div>
			)}

			<ProjectFormModal
				open={openCreate}
				submitting={creating}
				onCancel={() => setOpenCreate(false)}
				onSubmit={handleCreate}
			/>

			<ProjectFormModal
				open={!!editing}
				title="Sửa dự án"
				submitText="Lưu"
				submitting={updating}
				defaultValues={editing ? {name: editing.name, root_url: editing.root_url} : undefined}
				onCancel={() => setEditing(null)}
				onSubmit={handleUpdate}
			/>
		</div>
	);
}

export default ProjectList;
